import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Table } from '@/common';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE } from '@/constants';
import { PERMISSION_TYPE, PlaylistType } from '@/enums';
import { getDevicePlaylistsColumns, mapPlaylistSimpleToTableData } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { useUpdateDeviceScheduleContentMutation } from '@/services';
import { useGetAllPlaylists } from '@/services/playlist.service';
import { useFilterStore } from '@/store';
import { AllowedPermissions, DeviceSchedule, Playlist, TableData } from '@/types';

interface PlaylistTabProps {
  deviceSchedule: DeviceSchedule;
  requestSlotInterruptionWarning: (action: () => void | Promise<void>) => boolean;
  refetchDeviceSchedules?: () => void;
}

export const PlaylistTab = ({
  deviceSchedule,
  requestSlotInterruptionWarning,
  refetchDeviceSchedules
}: PlaylistTabProps) => {
  const { t } = useTranslation('common');
  const { organizationFilter } = useFilterStore();
  const [formattedPlaylists, setFormattedPlaylists] = useState<TableData[]>([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState<TableData | null>(null);

  const getOrganizationId = () => {
    if (organizationFilter && organizationFilter.value) {
      return organizationFilter.value as number;
    }
    return undefined;
  };

  const {
    data: allPlaylists,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetAllPlaylists(PAGE_SIZE, '', '', false, getOrganizationId(), PlaylistType.MIX);

  const { mutateAsync: updateDeviceScheduleContent } = useUpdateDeviceScheduleContentMutation(
    queryClient,
    {
      onSuccess: () => {
        if (refetchDeviceSchedules) {
          refetchDeviceSchedules();
        }
        showToast(ToastType.SUCCESS, t('deviceScheduleUpdatedSuccessfully'));
      },
      onError: () => {
        showToast(ToastType.ERROR, t('somethingWentWrong'));
      }
    }
  );

  useEffect(() => {
    if (allPlaylists?.pages) {
      const formattedData: TableData[] = allPlaylists?.pages.flatMap((page) =>
        page.data?.items.map(mapPlaylistSimpleToTableData)
      );
      setFormattedPlaylists(formattedData);
    }
  }, [allPlaylists]);

  useEffect(() => {
    if (deviceSchedule?.playlist) {
      setSelectedPlaylist({
        ...deviceSchedule.playlist,
        noOfAssets: deviceSchedule.playlist.numberOfAsset
      } as TableData);
    }
  }, [deviceSchedule]);

  const handleUnselectPlaylist = async () => {
    await updateDeviceScheduleContent({
      id: deviceSchedule.id,
      payload: {
        playlistId: null
      }
    });
    setSelectedPlaylist(null);
  };

  const handleSelectPlaylist = async (playlist: TableData) => {
    if (selectedPlaylist?.id === playlist.id) {
      return;
    }
    await updateDeviceScheduleContent({
      id: deviceSchedule.id,
      payload: {
        playlistId: playlist.id as number
      }
    });
    setSelectedPlaylist(playlist);
  };

  const executeIfSlotAvailable = (action: () => Promise<void>) => {
    if (requestSlotInterruptionWarning(action)) {
      return;
    }

    action();
  };

  const permissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_PLAYLISTS,
    editPermission: PERMISSION_TYPE.UPDATE_PLAYLISTS,
    deletePermission: PERMISSION_TYPE.DELETE_PLAYLISTS,
    viewPermission: PERMISSION_TYPE.VIEW_PLAYLISTS
  };

  return (
    <div className='flex h-full flex-col gap-4'>
      {selectedPlaylist && (
        <div className=''>
          <div className='text-xs text-secondary-gray'>{t('selectedPlaylist')}</div>
          <Table
            columns={getDevicePlaylistsColumns()}
            data={[selectedPlaylist]}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            hiddenColumnsMobile={[]}
            permissions={permissions}
            onSelect={() => executeIfSlotAvailable(handleUnselectPlaylist)}
            numberOfAppliedFilters={0}
            containerClassName='!mx-0'
            selectedRowIds={[selectedPlaylist.id?.toString() || '']}
          />
        </div>
      )}
      <div className='flex-1 overflow-y-auto'>
        <div className='text-xs text-secondary-gray'>{t('allAvailable')}</div>
        <Table
          columns={getDevicePlaylistsColumns()}
          data={formattedPlaylists}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          hiddenColumnsMobile={[]}
          permissions={permissions}
          numberOfAppliedFilters={0}
          handleViewDetails={(value) => executeIfSlotAvailable(() => handleSelectPlaylist(value))}
          containerClassName='!mx-0'
          isRowDisabled={(row) => (row as Playlist).noOfAssets === 0}
        />
      </div>
    </div>
  );
};
